import React, { ReactNode, useCallback } from 'react';
import { Box } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import TextLink from '@components/TextLink/TextLink';
import { RevisionAlertBodyProps } from './RevisionAlertBodyProps';
import { RevisionStatus } from '@interfaces/SurgeryRequest/RevisionStatusEnum';
import { AlertDisplayField } from '@components/Alerts/AlertDisplayField/AlertDisplayField';
import { AlertBody } from '@components/Alerts/AlertBody/AlertBody';
import RequestRevisionModal from '@components/CaseDetails/RequestRevisionModal/RequestRevisionModal';
import { utcToLocalTime } from '@utilities/dateUtilities';
import useHasPermission from '@utilities/hooks/useHasPermission/useHasPermission';
import { PermittedAction } from '@utilities/PermittedAction';
import useGetAccessor from '@components/CaseDetails/CaseInUse/hooks/useGetAccessor/useGetAccessor';
import { UserAccessEnum } from '@interfaces/SurgeryRequest/UserAccessEnum';
import { useModalContext } from '@components/Modal/ModalContextProvider';
import ModalViewer from '@components/Modal/ModalViewer';
import { REQUEST_REVISION_MODAL } from '@components/Modal/modalConstants';
import { defaultCallback } from '@utilities/defaultCallback';

const useStyles = makeStyles((theme) => createStyles({
	declineLink: {
		marginRight: theme.spacing(1.25),
	},
	details: {
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
	},
	linksContainer: {
		flex: '1 0 auto',
	},
}));

export const RevisionAlertBody = ({
	revision: {
		id: revisionId,
		reason,
		message,
		dateCreated,
		status,
		userCreated,
	},
	onMarkApproved,
	onMarkDeclined,
}: RevisionAlertBodyProps) => {
	const classes = useStyles();
	const {
		firstName,
		lastName,
	} = userCreated;

	const convertedDate = utcToLocalTime(dateCreated);

	const hasInteractiveRevisionAlertPermission = useHasPermission(PermittedAction.ACKNOWLEDGE_REVISION_ALERT);
	const {
		isAccessorDifferentUser: caseInUse,
	} = useGetAccessor([UserAccessEnum.CONFIRMER]);

	const handleDeclined = useCallback(() => {
		onMarkDeclined(revisionId, false);
	}, [onMarkDeclined, revisionId]);

	const handleApproved = useCallback(() => {
		onMarkApproved(revisionId, true);
	}, [onMarkApproved, revisionId]);

	const { openModal, closeModal } = useModalContext();

	const requestRevisionModalId = REQUEST_REVISION_MODAL + revisionId;

	const requestRevisionModalWrapper = (
		<ModalViewer id={requestRevisionModalId}>
			<RequestRevisionModal close={()=> closeModal(requestRevisionModalId)} handleDeclined={ handleDeclined }/>
		</ModalViewer>
	);

	const handleOpenRequestRevisionModal = useCallback(() => {
		openModal(requestRevisionModalId);
	}, [openModal, requestRevisionModalId]);

	const links: ReactNode | ReactNode[] =
		status === RevisionStatus.REVISED &&
		hasInteractiveRevisionAlertPermission &&
		(
			<Box>
				<TextLink
					className={classes.declineLink}
					disabled={caseInUse}
					onClick={caseInUse ? defaultCallback : handleOpenRequestRevisionModal}
				>
					Decline
				</TextLink>
				<TextLink
					disabled={caseInUse}
					onClick={caseInUse ? defaultCallback : handleApproved}
				>
					Approve
				</TextLink>
			</Box>
		);

	return (
		<>
			<AlertBody
				alertUser={firstName + ' ' + lastName}
				alertTime={convertedDate}
				body={
					<>
						<Box className={classes.details}>
							<AlertDisplayField
								label="Revision Reason:"
							>
								{reason}
							</AlertDisplayField>
							<AlertDisplayField
								label="Message:"
							>
								{message}
							</AlertDisplayField>
						</Box>
						<Box className={classes.linksContainer}>
							{links}
						</Box>
					</>
				}
			/>
			{requestRevisionModalWrapper}
		</>
	);
};
